/**
 * tiscali.cz — TEK sayfa (2026-09-16 operatör isteği): stok, kimlik doğrulama,
 * site başına satışlar ve mailbox başına "hangi siteler için satıldı" detayı,
 * import ve yenile — hepsi burada. (Alias-model şablonu bilinçli olarak
 * kullanılmaz: tiscalicz'da alias yok, satış hakkı (mailbox, site) bazındadır.)
 *
 * 2026-09-19 (operatör kararı): tiscali.cz'de IMAP ve çalışan POP3S yok; posta
 * POP3 port 110 (şifresiz) ile okunur ve ASLA doğrudan değil, residential SOCKS5
 * gateway'i üzerinden bağlanılır. Gateway bu sayfadan düzenlenir (ortak kart,
 * `protocol="POP3"` → "POP3 Proxy (SOCKS5)"); kullanıcı adındaki `{session}`
 * her kutuya sabit çıkış IP'si verir.
 */
import { useCallback, useEffect, useState } from 'react';

import { useCan } from '../../session/can.js';
import { BulkImportModal } from '../mailcom/BulkImportModal.js';
import { MailboxUnitDashboard } from '../mailbox-unit/MailboxUnitDashboard.js';
import { MailboxUnitManager } from '../mailbox-unit/MailboxUnitManager.js';
import { ImapGatewayCard } from '../mailbox-unit/ImapGatewayCard.js';
import {
  availableShare,
  fetchTiscaliczSiteUsage,
  type TiscaliczSiteUsage,
} from './siteUsage.js';

import '../../styles/mailboxunit.mobile.css';

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly data: TiscaliczSiteUsage }
  | { readonly kind: 'error'; readonly message: string };



export function TiscaliczPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const canManage = useCan('provider:manage_inventory');
  const [showImport, setShowImport] = useState(false);

  // `background`: a refresh after a manager mutation keeps the page (and so
  // the manager with its page / filters / feedback) mounted instead of
  // flipping to the loading state, which unmounted it and reset it to page 1.
  const load = useCallback(async (signal?: AbortSignal, opts?: { readonly background?: boolean }) => {
    if (opts?.background === true) setState((prev) => (prev.kind === 'ok' ? prev : { kind: 'loading' }));
    else setState({ kind: 'loading' });
    const res = await fetchTiscaliczSiteUsage(signal);
    if (res.kind === 'ok') {
      setState({ kind: 'ok', data: res.data });
      return;
    }
    if (res.kind === 'auth_required') {
      setState({ kind: 'error', message: 'Oturum gerekli — lütfen admin girişi yapın.' });
      return;
    }
    if (res.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Yetki yok: provider:inspect izni gerekiyor.' });
      return;
    }
    if (res.kind === 'network_error') {
      setState({ kind: 'error', message: 'Bağlantı hatası: ' + res.message });
      return;
    }
    setState({ kind: 'error', message: 'İstek başarısız (' + res.status + ': ' + res.reason + ').' });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);


  return (
    <section className="mbu-dashboard" data-testid="tiscalicz-dashboard">
      <header className="tiscalicz-head mbu-head">
        <div>
          <h1>tiscali.cz — stok, kimlik ve site kullanımı</h1>
          <p>
            Her satır bir mailbox'tır (alias üretilmez). Satış hakkı{' '}
            <strong>(mailbox, site)</strong> bazındadır: posta gelince o çift
            herkese kalıcı kapanır; posta gelmezse mailbox hemen başka müşterilere
            satılabilir, aynı müşteriye aynı site için bir daha satılmaz (D-247).
            Mailbox başka siteler için satılmaya devam eder. Bir mailbox,
            kimliği doğrulanmadan satılmaz.
          </p>
          {/* R-TISCALICZ-GATEWAY (2026-09-19): tiscali.cz has no IMAP and its
              POP3S port fails, so the runtime reads POP3 on port 110 in plaintext
              — always through the residential gateway below, never direct (no
              gateway = no login, fail closed). POP3 must re-login to see new
              mail; the {session} placeholder keeps those re-logins on one exit
              IP per mailbox. */}
          <p data-testid="tiscalicz-transport-note" style={{ marginTop: 6 }}>
            Posta <strong>POP3</strong> (port 110, şifresiz — tiscali.cz IMAP ve çalışan POP3S
            sunmuyor) ile, aşağıdaki <strong>residential proxy gateway'i üzerinden</strong> okunur;
            POP3 yalnız gelen kutusunu görür. Yeni postayı görmek için periyodik yeniden giriş
            yapılır; kullanıcı adında {'{session}'} varsa her kutu aynı çıkış IP'sinde kalır.
            Gateway tanımlı değilse hiç giriş yapılmaz (doğrudan bağlantı yok).
          </p>
        </div>
        <div className="tiscalicz-head-actions mbu-head-actions">
          {state.kind === 'ok' && (
            <span className="badge badge-neutral">
              {state.data.accounts} hesap · {state.data.units.total} unit
            </span>
          )}
          {canManage ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowImport(true)}
              data-testid="tiscalicz-import"
              title="email:şifre satırları — mailbox'ın kendisi unit olarak eklenir; şifre ':' içerebilir"
            >
              Import / TXT
            </button>
          ) : null}
          <button type="button" className="btn btn-secondary" onClick={() => void load()} data-testid="tiscalicz-refresh">
            Yenile
          </button>
        </div>
      </header>
      {canManage ? (
        <BulkImportModal
          providerId="tiscalicz"
          providerLabel="tiscali.cz"
          open={showImport}
          onClose={() => setShowImport(false)}
          onCompleted={() => void load()}
        />
      ) : null}

      {/* R-TISCALICZ-GATEWAY (2026-09-19): the POP3 SOCKS5 gateway is editable
          here (shared card, same /imap-gateway endpoint as t-online.de);
          the saved value is authoritative for the mail runtime (the K8s Secret
          is only the fallback). Rendered outside the site-usage states so the
          operator can fix the proxy even when that read fails. */}
      <ImapGatewayCard providerId="tiscalicz" providerLabel="tiscali.cz" protocol="POP3" />

      {state.kind === 'loading' && (
        <div className="card">
          <div className="card-body" data-testid="tiscalicz-loading">Yükleniyor…</div>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="card">
          <div className="card-body" role="alert" data-testid="tiscalicz-error">
            <span className="badge badge-danger" style={{ marginRight: 8 }}>Hata</span>
            {state.message}
          </div>
        </div>
      )}

      {state.kind === 'ok' && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <div className="stat" data-testid="kpi-leased">
                <div className="stat-label">Kiralamada</div>
                <div className="stat-value">{state.data.units.leased}</div>
                <div className="stat-meta">şu an müşteride</div>
              </div>
              <div className="stat" data-testid="kpi-verified">
                <div className="stat-label">Kimlik doğrulandı</div>
                <div className="stat-value">{state.data.probe?.verified ?? '—'}</div>
                <div className="stat-meta">
                  {state.data.probe?.pending ?? 0} bekliyor · {state.data.probe?.quarantined ?? 0} karantina
                </div>
              </div>
              <div className="stat" data-testid="kpi-accounts">
                <div className="stat-label">Hesap</div>
                <div className="stat-value">{state.data.accounts}</div>
                <div className="stat-meta">import edilmiş tiscalicz hesabı</div>
              </div>
          </div>

          <MailboxUnitDashboard providerId="tiscalicz" providerLabel="tiscali.cz" />

          <MailboxUnitManager
            providerId="tiscalicz"
            providerLabel="tiscali.cz"
            onChanged={() => void load(undefined, { background: true })}
          />

          <div className="card">
            <div className="card-body">
              <div className="card-title" style={{ marginBottom: 6 }}>Kapasite</div>
              <p className="tiscalicz-footnote" data-testid="tiscalicz-capacity-note">
                {state.data.units.total} mailbox × sınırsız site hakkı. Şu an{' '}
                <strong>{state.data.units.available}</strong> mailbox satılabilir (%{availableShare(state.data.units)});
                yanmış çiftler yalnızca kendi sitesi için kapalı, mailbox'ın kendisi kapanmaz.
                Import edilen bir mailbox, kimlik doğrulaması (dakikada 10 hesap) geçince satışa açılır.
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
